import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiCheckCircle, FiAlertTriangle, FiXCircle, 
  FiZap, FiShield, FiTrendingUp, FiLayers, FiCpu
} from 'react-icons/fi';
import { useSolution } from '../../../context/SolutionContext';

const ValidationView = () => {
  const { state } = useSolution();
  const validation = state.validation || { issues: [], warnings: [], suggestions: [], isValid: true };
  
  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <FiShield className="w-6 h-6 text-blue-600" />
          AI Validation & Suggestions
        </h2>
        
        {/* Status Banner */}
        <div className={`p-6 rounded-xl mb-6 ${
          validation.isValid 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-4">
            {validation.isValid ? (
              <FiCheckCircle className="w-8 h-8 text-green-500" />
            ) : (
              <FiXCircle className="w-8 h-8 text-red-500" />
            )}
            <div>
              <div className={`text-xl font-bold ${
                validation.isValid ? 'text-green-700' : 'text-red-700'
              }`}>
                {validation.isValid ? 'Solution is Valid' : `${validation.issues.length} Issues Found`}
              </div>
              <div className="text-sm text-gray-600">
                {validation.warnings?.length || 0} warnings, {validation.suggestions?.length || 0} suggestions
              </div>
            </div>
          </div>
        </div>
        
        {/* Issues */}
        {validation.issues?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
              <FiXCircle className="w-5 h-5" /> Issues ({validation.issues.length})
            </h3>
            <div className="space-y-3">
              {validation.issues.map((issue, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-sm text-red-800">{issue}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Warnings */}
        {validation.warnings?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-yellow-700 mb-4 flex items-center gap-2">
              <FiAlertTriangle className="w-5 h-5" /> Warnings ({validation.warnings.length})
            </h3>
            <div className="space-y-3">
              {validation.warnings.map((warning, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <p className="text-sm text-yellow-800">{warning}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* AI Suggestions */}
        {validation.suggestions?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-700 mb-4 flex items-center gap-2">
              <FiCpu className="w-5 h-5" /> AI Suggestions ({validation.suggestions.length})
            </h3>
            <div className="space-y-4">
              {validation.suggestions.map((suggestion, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold mt-0.5 ${
                      suggestion.priority === 'High' 
                        ? 'bg-red-100 text-red-700' 
                        : suggestion.priority === 'Medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {suggestion.priority || 'Info'}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm text-gray-800 font-medium">{suggestion.message}</p>
                      
                      {suggestion.recommendedComponents?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-gray-600 mb-2">Recommended Components:</p>
                          <div className="flex flex-wrap gap-2">
                            {suggestion.recommendedComponents.map(rec => (
                              <span key={rec.id} className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs">
                                {rec.icon} {rec.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {suggestion.alternatives?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-gray-600 mb-2">Alternatives:</p>
                          <div className="flex flex-wrap gap-2">
                            {suggestion.alternatives.map(alt => (
                              <span key={alt.id} className="inline-flex items-center gap-1 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs">
                                {alt.icon} {alt.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Layer Coverage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiLayers className="w-5 h-5 text-blue-600" /> Layer Coverage
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {['layer1', 'layer2', 'layer3', 'layer4', 'layer5', 'layer6', 'layer7', 'layer8', 'layer9', 'layer10', 'layer11', 'layer12'].map(layerId => {
              const isCovered = state.selectedComponents.some(c => c.layerId === layerId);
              const layerComponents = state.selectedComponents.filter(c => c.layerId === layerId);
              
              return (
                <div
                  key={layerId}
                  className={`p-4 rounded-lg border ${
                    isCovered 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Layer {layerId.slice(5)}
                    </span>
                    {isCovered ? (
                      <FiCheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <FiXCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  {isCovered && (
                    <div className="text-xs text-gray-500 mt-1">
                      {layerComponents.length} components
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Cost Optimization */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiTrendingUp className="w-5 h-5 text-green-600" /> Cost Summary
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <div className="text-sm text-gray-500">Min Cost</div>
              <div className="text-xl font-bold text-green-600">
                ${state.totalCost?.low?.toFixed(0) || '0'}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <div className="text-sm text-gray-500">Max Cost</div>
              <div className="text-xl font-bold text-green-600">
                ${state.totalCost?.high?.toFixed(0) || '0'}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <div className="text-sm text-gray-500">Avg Cost</div>
              <div className="text-xl font-bold text-green-600">
                ${(((state.totalCost?.low || 0) + (state.totalCost?.high || 0)) / 2).toFixed(0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationView;